import type { BlockerFunction } from 'react-router';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';

import { AlertDialog, Button } from '~/components';
import { useBrowserBlocker } from '~/components/Blocker/Blocker.hooks';

type Props = {
  isDirty: boolean;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => Promise<boolean>;
};

export const SettingsBlocker = ({ isDirty, isSaving, onDiscard, onSave }: Props) => {
  const { t } = useTranslation(['common', 'settings']);

  useBrowserBlocker(isDirty);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      if (nextLocation.state?.blockable === false) return false;

      if (currentLocation.pathname === nextLocation.pathname) return false;

      return isDirty;
    },
    [isDirty],
  );

  const blocker = useBlocker(shouldBlock);

  const saveAndProceed = async () => {
    const isSaved = await onSave();

    if (isSaved) blocker.proceed?.();
  };

  const discardAndProceed = () => {
    onDiscard();
    blocker.proceed?.();
  };

  return (
    <AlertDialog.Root onOpenChange={(open) => !open && !isSaving && blocker.reset?.()} open={blocker.state === 'blocked'}>
      <AlertDialog.Body status="warning">
        <AlertDialog.Title>{t('settings:unsavedChanges.title')}</AlertDialog.Title>

        <AlertDialog.Description>{t('settings:unsavedChanges.description')}</AlertDialog.Description>

        <AlertDialog.Actions>
          <Button disabled={isSaving} emphasis="secondary" icon="Undo" onClick={discardAndProceed}>
            {t('settings:unsavedChanges.discard')}
          </Button>

          <Button icon="Save" loading={isSaving} onClick={saveAndProceed}>
            {t('settings:unsavedChanges.save')}
          </Button>
        </AlertDialog.Actions>
      </AlertDialog.Body>
    </AlertDialog.Root>
  );
};
